import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Alert,
  Button,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  Grid,
  Switch,
  TextField,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import { MenuBook as DocsIcon, RestartAlt as RestartIcon } from '@mui/icons-material';
import { api, endpoints } from '../services/api';
import { devicesApi } from '../services/devicesApi';
import { useAuth } from '../contexts/AuthContext';
import { getCapabilitiesFromUser, hasCapability } from '../auth/access';
import { FirmwareReadinessPanel } from '../components/FirmwareReadinessPanel';
import { useMode } from '../contexts/ModeContext';
import { OcrPreflightPanel } from '../components/OcrPreflightPanel';
import SuggestedActionsRail from '../components/SuggestedActionsRail';
import { WarehouseSelect } from '../components/ui/WarehouseSelect';

type FleetScanResult = {
  ok?: boolean;
  started_at?: string;
  finished_at?: string;
  subset?: string;
  concurrency?: number;
  scanned?: number;
  summary?: {
    ok_devices?: number;
    warn_devices?: number;
    fail_devices?: number;
    unreachable_devices?: number;
  };
  results?: any[];
  error?: string;
  detail?: string;
};

type Device = {
  id: string;
  device_id: string;
  name: string;
  site_id?: string;
  ip_address?: string;
  status?: string;
};

type Camera = {
  camera_id: string;
  device_id: string;
  display_name?: string;
  slug?: string;
  source?: Record<string, any>;
};

// Helper to detect UUIDs (database IDs, not canonical device_ids)
const _isUuid = (s: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test((s || '').trim());

const _isIpv4Literal = (s: string): boolean =>
  /^\d{1,3}(?:\.\d{1,3}){3}$/.test(String(s || '').trim());

const Preflight: React.FC = () => {
  const navigate = useNavigate();
  const { effectiveUser: user, isLoading: authLoading } = useAuth();
  const capabilities = useMemo(() => getCapabilitiesFromUser(user), [user]);
  const canWriteDevices = hasCapability(capabilities, 'devices:write');
  const { mode } = useMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Phase 3: convergence rollup (cloud-side, DB-derived)
  const [convergenceLoading, setConvergenceLoading] = useState(false);
  const [convergenceError, setConvergenceError] = useState<string | null>(null);
  const [convergence, setConvergence] = useState<any | null>(null);

  // Support both ?device=<id> (canonical) and legacy ?device_id=<id>
  // F-32: Ignore UUIDs (database IDs) - only use canonical device_ids
  const deviceParam = searchParams.get('device') || searchParams.get('device_id') || '';
  const selectedDeviceId = deviceParam && !_isUuid(deviceParam) ? deviceParam : '';
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [camerasLoading, setCamerasLoading] = useState(false);
  const [liveViewStatus, setLiveViewStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [liveViewResults, setLiveViewResults] = useState<Record<string, any> | null>(null);
  const [liveViewReportText, setLiveViewReportText] = useState<string | null>(null);
  const [liveViewError, setLiveViewError] = useState<string | null>(null);
  const [restartBusy, setRestartBusy] = useState(false);
  const [restartResult, setRestartResult] = useState<{
    level: 'success' | 'warning' | 'error';
    message: string;
    details?: any;
  } | null>(null);

  // Edge onboarding (deploy_runner: scripts/onboard_edge_unit.sh)
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [onboardLoading, setOnboardLoading] = useState(false);
  const [onboardResult, setOnboardResult] = useState<any | null>(null);
  const [onboardEdgeHost, setOnboardEdgeHost] = useState<string>('');
  const [onboardCloudHost, setOnboardCloudHost] = useState<string>('');
  const [onboardCloudApiUrl, setOnboardCloudApiUrl] = useState<string>('');
  const [onboardCloudMqttPort, setOnboardCloudMqttPort] = useState<string>('8883');
  const [onboardEdgePassword, setOnboardEdgePassword] = useState<string>('');
  const [oneClickOpen, setOneClickOpen] = useState(false);
  const [oneClickLoading, setOneClickLoading] = useState(false);
  const [oneClickJob, setOneClickJob] = useState<any | null>(null);
  const [oneClickError, setOneClickError] = useState<string | null>(null);
  const [oneClickRunOnboard, setOneClickRunOnboard] = useState<boolean>(true);
  const [oneClickRequireGatePass, setOneClickRequireGatePass] = useState<boolean>(true);

  // Fleet drift scan
  const [fleetOpen, setFleetOpen] = useState(false);
  const [fleetLoading, setFleetLoading] = useState(false);
  const [fleetResult, setFleetResult] = useState<FleetScanResult | null>(null);

  useEffect(() => {
    if (!authLoading && user) void fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  // F-32: Clean up UUID from URL if present
  useEffect(() => {
    const deviceParam = searchParams.get('device') || searchParams.get('device_id') || '';
    if (deviceParam && _isUuid(deviceParam)) {
      const next = new URLSearchParams(searchParams);
      next.delete('device');
      next.delete('device_id');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!authLoading && user && selectedDeviceId) void fetchCameras(selectedDeviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, selectedDeviceId]);

  useEffect(() => {
    const id = selectedDeviceId;
    if (!id || !user || authLoading) return;
    const run = async () => {
      setConvergenceLoading(true);
      setConvergenceError(null);
      try {
        const res = await api.post('/api/v1/deployments/clarity/convergence_batch', { device_ids: [id], limit: 1 });
        const item = Array.isArray(res.data?.items) ? res.data.items[0] : null;
        setConvergence(item || null);
      } catch (e: any) {
        setConvergence(null);
        setConvergenceError(e?.response?.data?.error || e?.message || 'convergence_failed');
      } finally {
        setConvergenceLoading(false);
      }
    };
    void run();
  }, [selectedDeviceId, user, authLoading]);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      setError(null);
      const items = (await devicesApi.listDevices()) as Device[];
      setDevices(items);
      // Auto-select first device if none selected (and URL doesn't have a UUID).
      if (!selectedDeviceId && items.length > 0) {
        const next = new URLSearchParams(searchParams);
        next.set('device', items[0].device_id);
        setSearchParams(next, { replace: true });
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const fetchCameras = async (deviceId: string) => {
    try {
      setCamerasLoading(true);
      setLiveViewResults(null);
      setLiveViewReportText(null);
      setLiveViewStatus('idle');
      setLiveViewError(null);
      const res = await api.get(`/api/v1/registry/devices/${deviceId}/cameras`);
      const items: Camera[] = res.data?.items || [];
      setCameras(items);
    } catch (e: any) {
      setCameras([]);
      setLiveViewError(e?.response?.data?.error || e?.message || 'Failed to load cameras');
    } finally {
      setCamerasLoading(false);
    }
  };

  const runLiveViewVerification = async () => {
    if (!selectedDeviceId) return;
    if (cameras.length === 0) {
      setLiveViewError('No cameras found for selected device');
      return;
    }
    try {
      setLiveViewStatus('running');
      setLiveViewError(null);
      // F-06: Increase timeout for live view verification (can take 60-90s for multiple cameras)
      const resp = await api.post(`/api/v1/preflight/devices/${selectedDeviceId}/live_view`, {
        site_id: selected?.site_id || 'default',
        timeout_sec: 30,  // Per-camera timeout; total can be 60-90s for 2 cameras
      }, {
        timeout: 120000,  // 120 seconds total timeout for axios
      });
      const results: Record<string, any> = resp.data?.results || {};
      setLiveViewResults(results);
      setLiveViewReportText(resp.data?.report_text || null);
      setLiveViewStatus('done');
    } catch (e: any) {
      setLiveViewStatus('error');
      setLiveViewError(e?.response?.data?.error || e?.message || 'Live View verification failed');
    }
  };

  const restartEdgeRuntime = async () => {
    if (!selectedDeviceId) return;
    setRestartBusy(true);
    setRestartResult(null);
    try {
      const res = await api.post(
        `/api/v1/pipeline/devices/${encodeURIComponent(selectedDeviceId)}/edge/orchestrator/restart`,
        {},
      );
      setRestartResult({
        level: res?.data?.ok === true ? 'success' : 'warning',
        message:
          res?.data?.ok === true
            ? 'Edge runtime restart requested.'
            : 'Edge runtime restart requested. Check response details if the unit does not recover.',
        details: res?.data,
      });
    } catch (e: any) {
      setRestartResult({
        level: 'error',
        message:
          e?.response?.data?.detail ||
          e?.response?.data?.error ||
          e?.message ||
          'edge_runtime_restart_failed',
      });
    } finally {
      setRestartBusy(false);
    }
  };

  const _openOnboardDialog = () => {
    setOnboardResult(null);
    if (!onboardEdgeHost) {
      try {
        const ip = String((selected as any)?.ip_address || '').trim();
        if (ip) setOnboardEdgeHost(ip);
      } catch {
        // ignore
      }
    }
    if (!onboardCloudHost) {
      try {
        const h = String(window.location.hostname || '').trim();
        if (h && !_isIpv4Literal(h)) setOnboardCloudHost(h);
      } catch {
        // ignore
      }
    }
    if (!onboardCloudApiUrl) {
      try {
        const h = String(window.location.hostname || '').trim();
        if (h && !_isIpv4Literal(h)) setOnboardCloudApiUrl(`https://${h}:8443`);
      } catch {
        // ignore
      }
    }
    setOnboardOpen(true);
  };

  const runOnboardEdge = async () => {
    if (!selectedDeviceId) return;
    setOnboardLoading(true);
    setOnboardResult(null);
    try {
      const payload: any = {
        edge_host: (onboardEdgeHost || '').trim() || undefined,
        cloud_host: (onboardCloudHost || '').trim() || undefined,
        cloud_api_url: (onboardCloudApiUrl || '').trim() || undefined,
        cloud_mqtt_port: (onboardCloudMqttPort || '').trim() || undefined,
        edge_password: (onboardEdgePassword || '').trim() || undefined,
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      const res = await api.post(`/api/v1/pipeline/devices/${encodeURIComponent(selectedDeviceId)}/edge/onboard`, payload);
      setOnboardResult(res.data || null);
      try {
        const host = String((res.data as any)?.edge_ip || (payload as any)?.edge_host || '').trim();
        if (host) localStorage.setItem(`edge_host_override:${selectedDeviceId}`, host);
      } catch {
        // ignore
      }
    } catch (e: any) {
      setOnboardResult({
        ok: false,
        error: e?.response?.data?.error || e?.message || 'onboard_failed',
        detail: e?.response?.data?.detail,
      });
    } finally {
      setOnboardLoading(false);
    }
  };

  const _openOneClickDialog = async () => {
    setOneClickError(null);
    if (!onboardEdgeHost) {
      try {
        const ip = String((selected as any)?.ip_address || '').trim();
        if (ip) setOnboardEdgeHost(ip);
      } catch {
        // ignore
      }
    }
    if (!onboardCloudHost) {
      try {
        const h = String(window.location.hostname || '').trim();
        if (h && !_isIpv4Literal(h)) setOnboardCloudHost(h);
      } catch {
        // ignore
      }
    }
    if (!onboardCloudApiUrl) {
      try {
        const h = String(window.location.hostname || '').trim();
        if (h && !_isIpv4Literal(h)) setOnboardCloudApiUrl(`https://${h}:8443`);
      } catch {
        // ignore
      }
    }
    setOneClickOpen(true);
    if (!selectedDeviceId) return;
    try {
      const res = await api.get(endpoints.commission.latest(selectedDeviceId));
      if (res.data?.ok !== false || res.data?.error !== 'no_jobs') {
        setOneClickJob(res.data || null);
      }
    } catch {
      // ignore
    }
  };

  const runOneClickCommission = async () => {
    if (!selectedDeviceId) return;
    setOneClickLoading(true);
    setOneClickError(null);
    try {
      const payload: any = {
        edge_host: (onboardEdgeHost || '').trim() || undefined,
        cloud_host: (onboardCloudHost || '').trim() || undefined,
        cloud_api_url: (onboardCloudApiUrl || '').trim() || undefined,
        cloud_mqtt_port: (onboardCloudMqttPort || '').trim() || undefined,
        edge_password: (onboardEdgePassword || '').trim() || undefined,
        run_onboard: oneClickRunOnboard,
        run_desired_state_sync: true,
        require_gate_pass: oneClickRequireGatePass,
        setup_payload: { auto_fix: true },
        preflight_payload: {},
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      const res = await api.post(
        endpoints.commission.fromZero(selectedDeviceId),
        payload
      );
      setOneClickJob(res.data || null);
      try {
        const host = String((payload as any)?.edge_host || '').trim();
        if (host) localStorage.setItem(`edge_host_override:${selectedDeviceId}`, host);
      } catch {
        // ignore
      }
    } catch (e: any) {
      setOneClickError(e?.response?.data?.error || e?.message || 'commission_one_click_failed');
    } finally {
      setOneClickLoading(false);
    }
  };

  const cancelOneClickCommission = async () => {
    if (!selectedDeviceId) return;
    const jobId = String(oneClickJob?.job_id || oneClickJob?.run_id || '').trim();
    if (!jobId) return;
    try {
      await api.post(endpoints.commission.cancel(selectedDeviceId, jobId), {});
      const res = await api.get(endpoints.commission.status(selectedDeviceId, jobId));
      setOneClickJob(res.data || null);
    } catch (e: any) {
      setOneClickError(e?.response?.data?.error || e?.message || 'cancel_failed');
    }
  };

  const refreshOneClickCommission = async () => {
    if (!selectedDeviceId) return;
    const jobId = String(oneClickJob?.job_id || oneClickJob?.run_id || '').trim();
    if (!jobId) return;
    try {
      const res = await api.get(endpoints.commission.status(selectedDeviceId, jobId));
      setOneClickJob(res.data || null);
    } catch (e: any) {
      setOneClickError(e?.response?.data?.error || e?.message || 'refresh_failed');
    }
  };

  const oneClickJobId = String(oneClickJob?.job_id || oneClickJob?.run_id || '').trim();
  const oneClickStatus = String(oneClickJob?.status || '').trim().toLowerCase();
  const oneClickTerminal = oneClickStatus === 'succeeded' || oneClickStatus === 'failed' || oneClickStatus === 'cancelled';

  useEffect(() => {
    if (!selectedDeviceId || !oneClickJobId || oneClickTerminal || oneClickStatus !== 'running') return;
    const timer = window.setInterval(async () => {
      try {
        const res = await api.get(endpoints.commission.status(selectedDeviceId, oneClickJobId));
        setOneClickJob(res.data || null);
      } catch {
        // ignore intermittent poll failures
      }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [selectedDeviceId, oneClickJobId, oneClickStatus, oneClickTerminal]);

  const runFleetScan = async () => {
    setFleetOpen(true);
    setFleetLoading(true);
    setFleetResult(null);
    try {
      const res = await api.post('/api/v1/preflight/fleet_scan', {
        subset: 'cloud,odoo,tls,mqtt',
        concurrency: 4,
        timeout_sec: 45,
      });
      setFleetResult(res.data || null);
    } catch (e: any) {
      setFleetResult({
        ok: false,
        error: e?.response?.data?.error || e?.message || 'fleet_scan_failed',
        detail: e?.response?.data?.detail,
      });
    } finally {
      setFleetLoading(false);
    }
  };

  const copyResults = async () => {
    if (!liveViewResults && !liveViewReportText) return;
    try {
      const text =
        liveViewReportText ||
        (liveViewResults ? JSON.stringify(liveViewResults, null, 2) : '');
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore
    }
  };

  const selected = useMemo(() => devices.find((d) => d.device_id === selectedDeviceId) || null, [devices, selectedDeviceId]);
  const deviceSelectOptions = useMemo(
    () =>
      devices.map((d) => ({
        value: d.device_id,
        label: d.name || d.device_id,
        secondary: d.device_id,
      })),
    [devices]
  );
  const edgeHostOverride = useMemo(() => {
    const did = String(selectedDeviceId || '').trim();
    if (!did) return '';
    try {
      return String(localStorage.getItem(`edge_host_override:${did}`) || '').trim();
    } catch {
      return '';
    }
  }, [selectedDeviceId]);
  const [edgeHostManual, setEdgeHostManual] = useState<string>('');

  // Keep a per-device edge host override (dynamic networks; not identity).
  useEffect(() => {
    const did = String(selectedDeviceId || '').trim();
    if (!did) {
      setEdgeHostManual('');
      return;
    }
    const fallback = String(selected?.ip_address || edgeHostOverride || '').trim();
    setEdgeHostManual((prev) => (String(prev || '').trim() ? prev : fallback));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceId, selected?.ip_address]);

  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="text.secondary">
          Please log in to view preflight.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} lg={8}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Typography variant="h4">Setup readiness</Typography>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Tooltip title="Open documentation (Preflight)">
            <IconButton
              size="small"
              component="a"
              href="/docs/user-guide/configuration/preflight/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <DocsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <WarehouseSelect
            label="Select device"
            value={selectedDeviceId}
            onChange={(deviceId) => setSearchParams({ device: deviceId })}
            options={deviceSelectOptions}
            disabled={loading}
            minWidth={320}
            data-testid="preflight-device-select"
          />
          <Button
            variant="contained"
            color="success"
            onClick={() => navigate(selectedDeviceId ? `/bringup?device=${encodeURIComponent(selectedDeviceId)}` : '/bringup')}
            disabled={loading}
          >
            Open deployment readiness
          </Button>
          <Button variant="outlined" onClick={runFleetScan} disabled={loading || devices.length === 0}>
            Fleet Scan
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Deployment readiness is the commissioning flow inside Setup. Use this page to validate cameras, zones, and
        policy packs ready for deployment, or to troubleshoot setup readiness failures.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Onboard Edge Dialog */}
      <Dialog open={onboardOpen} onClose={() => setOnboardOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Onboard Edge Device (mTLS)</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Runs the operator onboarding script via deploy runner (issues split API+MQTT client certs, copies PKI, writes
            `/etc/edgentik/edgentik.env` with split mTLS paths, updates orchestrator override, and deploys edge-services).
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Edge Host/IP (current)"
                value={onboardEdgeHost}
                onChange={(e) => setOnboardEdgeHost(e.target.value)}
                placeholder="e.g. edge-host or IP"
                helperText="Do not bind device identity to a static IP. Provide the edge’s current reachable host/IP for onboarding."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cloud Host/IP (edge-facing)"
                value={onboardCloudHost}
                onChange={(e) => setOnboardCloudHost(e.target.value)}
                placeholder="e.g. cloud-host or IP"
                helperText="Leave blank to use the cloud hostname."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cloud API URL (optional override)"
                value={onboardCloudApiUrl}
                onChange={(e) => setOnboardCloudApiUrl(e.target.value)}
                placeholder="e.g. https://cloud-host:8443"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cloud MQTT TLS Port"
                value={onboardCloudMqttPort}
                onChange={(e) => setOnboardCloudMqttPort(e.target.value)}
                placeholder="8883"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Edge SSH password (optional, first-boot only)"
                value={onboardEdgePassword}
                onChange={(e) => setOnboardEdgePassword(e.target.value)}
                placeholder="leave blank if key-based SSH already works"
                helperText="Used only to bootstrap SSH key access on brand new devices. Not required for already-onboarded units."
              />
            </Grid>
          </Grid>
          {onboardResult && (
            <Alert severity={onboardResult.ok ? 'success' : 'warning'} sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Onboard result</Typography>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(onboardResult, null, 2)}</pre>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOnboardOpen(false)} disabled={onboardLoading}>
            Close
          </Button>
          <Button variant="contained" onClick={runOnboardEdge} disabled={!selectedDeviceId || onboardLoading}>
            {onboardLoading ? 'Onboarding…' : 'Run Onboard'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* One-Click Commissioning Dialog */}
      <Dialog open={oneClickOpen} onClose={() => setOneClickOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>One-Click Wiped Unit Commissioning</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Runs onboarding + setup + desired-state sync + final preflight and captures commissioning artifacts.
            Camera claim is now an explicit post-provision action on the Bring-Up page.
          </Alert>
          {oneClickError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {oneClickError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Edge Host/IP (current)"
                value={onboardEdgeHost}
                onChange={(e) => setOnboardEdgeHost(e.target.value)}
                placeholder="e.g. edge-host or IP"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cloud Host/IP (edge-facing)"
                value={onboardCloudHost}
                onChange={(e) => setOnboardCloudHost(e.target.value)}
                placeholder="e.g. cloud-host or IP"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cloud API URL (optional override)"
                value={onboardCloudApiUrl}
                onChange={(e) => setOnboardCloudApiUrl(e.target.value)}
                placeholder="e.g. https://cloud-host:8443"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cloud MQTT TLS Port"
                value={onboardCloudMqttPort}
                onChange={(e) => setOnboardCloudMqttPort(e.target.value)}
                placeholder="8883"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Edge SSH password (optional, first-boot only)"
                value={onboardEdgePassword}
                onChange={(e) => setOnboardEdgePassword(e.target.value)}
                placeholder="leave blank if key-based SSH already works"
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <FormControlLabel
                  control={<Switch checked={oneClickRunOnboard} onChange={(_, checked) => setOneClickRunOnboard(checked)} />}
                  label="Run onboard step"
                />
                <FormControlLabel
                  control={<Switch checked={oneClickRequireGatePass} onChange={(_, checked) => setOneClickRequireGatePass(checked)} />}
                  label="Require release gate pass"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                Camera claims are intentionally excluded from one-click to prevent accidental runtime drift.
                Use Bring-Up step "Add Cameras" after provisioning completes.
              </Alert>
            </Grid>
          </Grid>

          {oneClickJob && (
            <Box sx={{ mt: 2 }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Job</Typography>
                <Chip
                  size="small"
                  label={String(oneClickJob?.status || 'unknown')}
                  color={
                    oneClickStatus === 'succeeded'
                      ? 'success'
                      : oneClickStatus === 'failed'
                        ? 'error'
                        : oneClickStatus === 'cancelled'
                          ? 'default'
                          : 'info'
                  }
                />
                <Typography variant="caption" color="text.secondary">
                  {String(oneClickJob?.job_id || oneClickJob?.run_id || '')}
                </Typography>
              </Box>
              <List dense>
                {Array.isArray(oneClickJob?.steps) &&
                  oneClickJob.steps.map((s: any, idx: number) => (
                    <ListItem key={`${String(s?.step_id || 'step')}-${idx}`}>
                      <ListItemText
                        primary={`${String(s?.step_id || 'step')}: ${String(s?.status || 'unknown')}`}
                        secondary={String(s?.error_code || s?.error_detail || '')}
                      />
                    </ListItem>
                  ))}
              </List>
              <Box
                component="pre"
                sx={{
                  fontSize: 11,
                  fontFamily: 'monospace',
                  background: '#0b1020',
                  color: '#e6edf3',
                  padding: 1.5,
                  borderRadius: 1,
                  overflowX: 'auto',
                  maxHeight: 260,
                }}
              >
                {JSON.stringify(oneClickJob, null, 2)}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOneClickOpen(false)} disabled={oneClickLoading}>
            Close
          </Button>
          <Button onClick={refreshOneClickCommission} disabled={!oneClickJobId || oneClickLoading}>
            Refresh
          </Button>
          <Button color="warning" onClick={cancelOneClickCommission} disabled={!oneClickJobId || oneClickTerminal || oneClickLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={runOneClickCommission} disabled={!selectedDeviceId || oneClickLoading || oneClickStatus === 'running'}>
            {oneClickLoading ? 'Starting…' : oneClickStatus === 'running' ? 'Running…' : 'Run One-Click'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fleet Scan Dialog */}
      <Dialog open={fleetOpen} onClose={() => setFleetOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Fleet Scan (Drift)</DialogTitle>
        <DialogContent>
          {fleetLoading && (
            <Box display="flex" alignItems="center" gap={2} py={2}>
              <CircularProgress size={20} />
              <Typography variant="body2">Scanning devices…</Typography>
            </Box>
          )}
          {!fleetLoading && fleetResult?.ok === false && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {fleetResult.error || 'Fleet scan failed'}
              {fleetResult.detail ? ` — ${fleetResult.detail}` : ''}
            </Alert>
          )}
          {!fleetLoading && fleetResult?.ok && (
            <Box mt={1}>
              <Typography variant="body2" color="text.secondary">
                Scanned {fleetResult.scanned ?? '—'} devices. OK: {fleetResult.summary?.ok_devices ?? 0} • Warnings:{' '}
                {fleetResult.summary?.warn_devices ?? 0} • Failures: {fleetResult.summary?.fail_devices ?? 0} • Unreachable:{' '}
                {fleetResult.summary?.unreachable_devices ?? 0}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <List dense>
                {(fleetResult.results || []).map((r: any) => {
                  const did = String(r?.device_id || '').trim() || 'unknown-device';
                  const ok = !!r?.ok;
                  const failures = Array.isArray(r?.failures) ? r.failures.length : 0;
                  const warnings = Array.isArray(r?.warnings) ? r.warnings.length : 0;
                  const secondary = !ok
                    ? `UNREACHABLE: ${r?.error || ''} ${(r?.detail || '').toString().slice(0, 160)}`
                    : failures
                      ? `FAIL (${failures})`
                      : warnings
                        ? `WARN (${warnings})`
                        : 'OK';
                  return (
                    <ListItem key={did}>
                      <ListItemText primary={did} secondary={secondary} />
                    </ListItem>
                  );
                })}
              </List>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Tip: for a failing device, open it and click RUN PREFLIGHT for full details.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFleetOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {loading && !selected ? (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="text" width="40%" height={32} sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, mb: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        </Box>
      ) : selected ? (
        <>
          {!selected.ip_address && (
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Edge Host/IP (current)"
                value={edgeHostManual}
                onChange={(e) => {
                  const v = String(e.target.value || '');
                  setEdgeHostManual(v);
                  try {
                    const did = String(selectedDeviceId || '').trim();
                    const host = v.trim();
                    if (did && host) localStorage.setItem(`edge_host_override:${did}`, host);
                  } catch {
                    // ignore
                  }
                }}
                placeholder="e.g. edge-host or IP"
                helperText="This is a runtime reachability hint (dynamic). Device identity remains device_id."
              />
            </Box>
          )}
          <FirmwareReadinessPanel deviceId={selected.device_id} ipAddress={selected.ip_address || edgeHostManual.trim() || edgeHostOverride || undefined} />
          {mode === 'ocr' && <OcrPreflightPanel deviceId={selected.device_id} />}

          <Box mt={2}>
            <Typography variant="h6" gutterBottom>
              Convergence (desired vs applied)
            </Typography>
            {convergenceLoading && (
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Refreshing convergence…</Typography>
              </Box>
            )}
            {convergenceError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {convergenceError}
              </Alert>
            )}
            {!convergenceError && convergence && (
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  overall=<code>{String(convergence?.overall_status || 'unknown')}</code> • drb=
                  <code>{String(convergence?.drb?.status || 'unknown')}</code> • policies=
                  <code>{String(convergence?.policies?.status || 'unknown')}</code> • fcp=
                  <code>{String(convergence?.fcp?.status || 'unknown')}</code> • zones=
                  <code>
                    {String(convergence?.zones?.status || 'unknown')} ({String(convergence?.zones?.counts?.converged ?? 0)}/
                    {String(convergence?.zones?.counts?.cameras_total ?? 0)})
                  </code>
                </Typography>
              </Alert>
            )}
          </Box>

          <Box mt={3}>
            <Typography variant="h5" gutterBottom>
              Verify Live View (tri-probe)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Runs INGEST/OUTPUT/VIEW checks per camera and prints a copy/paste report.
            </Typography>

            {camerasLoading ? (
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Loading cameras…</Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Cameras: <strong>{cameras.length}</strong>
              </Typography>
            )}

            {liveViewError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {liveViewError}
              </Alert>
            )}

            {restartResult && (
              <Alert
                severity={restartResult.level}
                sx={{ mb: 1 }}
                onClose={() => setRestartResult(null)}
              >
                <Typography variant="body2">{restartResult.message}</Typography>
                {restartResult.details && (
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      mb: 0,
                      fontSize: 11,
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(restartResult.details, null, 2)}
                  </Box>
                )}
              </Alert>
            )}

            <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
              <Button variant="contained" onClick={runLiveViewVerification} disabled={liveViewStatus === 'running' || camerasLoading}>
                {liveViewStatus === 'running' ? 'Running…' : 'Run Live View Verification'}
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<RestartIcon />}
                onClick={restartEdgeRuntime}
                disabled={!selectedDeviceId || restartBusy || !canWriteDevices}
              >
                {restartBusy ? 'Restarting…' : 'Restart Edge Runtime'}
              </Button>
              <Button variant="outlined" onClick={copyResults} disabled={!liveViewResults}>
                Copy results
              </Button>
            </Box>
            {!canWriteDevices && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Restart requires `devices:write`.
              </Typography>
            )}

            {(liveViewReportText || liveViewResults) && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  F-06: One-click Preflight checklist with remediation actions
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontSize: 12,
                    fontFamily: 'monospace',
                    background: '#0b1020',
                    color: '#e6edf3',
                    padding: 2,
                    borderRadius: 1,
                    overflowX: 'auto',
                    overflowY: 'auto',
                    maxHeight: 600,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {liveViewReportText || JSON.stringify(liveViewResults, null, 2)}
                </Box>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <Alert severity="warning">No device selected.</Alert>
      )}
        </Grid>
        <Grid item xs={12} lg={4}>
          <SuggestedActionsRail context="preflight" deviceId={selectedDeviceId} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Preflight;
